'use client';

import {
    type Context,
    createContext,
    type PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { hasAutoshipInCart } from '@libs/common-next/utils';
import type { ItemUI } from '@libs/common-node';
import { isLocaleCA } from '@libs/core';

import { useAutoshipDecisions, useCart } from '../hooks';
import { getENV } from '../utils/env';
import {
    applyPercentageToEligibleCartItemsState,
    mergeCrawlAutoshipPercentagesState,
} from './utils/autoshipPercentageState';

interface AutoshipPercentageProps {
    readonly bannerPercentage: string;
    readonly itemPercentages: Record<string, string>;
    readonly isBannerVisible: boolean;
    readonly hideBanner: () => void;
}

type AutoshipPercentageContextValue = Nullable<AutoshipPercentageProps>;

type AutoshipPercentageGlobal = typeof globalThis & {
    __petmAutoshipPercentageContext?: Context<AutoshipPercentageContextValue>;
};

function getAutoshipPercentageContext() {
    const globalContext = globalThis as AutoshipPercentageGlobal;

    globalContext.__petmAutoshipPercentageContext ??= createContext<AutoshipPercentageContextValue>(null);

    return globalContext.__petmAutoshipPercentageContext;
}

export const AutoshipPercentageContext = getAutoshipPercentageContext();

const initialPercentage = isLocaleCA() ? '15%' : '35%';

const activeSubscriptionPercentage = '5%';
const bannerPercentage = '5%';

export function AutoshipPercentageProvider({ children }: PropsWithChildren) {
    const [itemPercentages, setItemPercentages] = useState<Record<string, string>>({});
    const [isBannerVisible, setIsBannerVisible] = useState(false);
    const { cart } = useCart();
    const autoshipDiscountUpdatesEnabled = getENV('NEXT_PUBLIC_AUTOSHIP_DISCOUNT_UPDATES_ENABLED');
    const wasBannerDismissed = useRef<boolean>(false);
    const { isAutoshipCrawl, isAutoshipPreCrawl } = useAutoshipDecisions();

    const applyPercentageToEligibleCartItems = (cartItems: ItemUI[], percentage: string) => {
        setItemPercentages(prevState => applyPercentageToEligibleCartItemsState(prevState, cartItems, percentage));
    };

    useEffect(() => {
        if (!cart) {
            return;
        }

        const items = cart.fulfillmentGroups.flatMap(({ items: groupItems }) => groupItems.map(cartItem => cartItem));

        if (autoshipDiscountUpdatesEnabled) {
            const hasAutoshipInCartValue = hasAutoshipInCart(cart);

            if (!hasAutoshipInCartValue) {
                setIsBannerVisible(false);
                wasBannerDismissed.current = false;
            }

            const hasAutoshipOrders = cart?.hasAutoshipOrders;

            if (hasAutoshipInCartValue && hasAutoshipOrders) {
                applyPercentageToEligibleCartItems(items, activeSubscriptionPercentage);

                if (cart?.id && !wasBannerDismissed.current) {
                    setIsBannerVisible(true);
                    wasBannerDismissed.current = true;
                }

                return;
            }

            if (hasAutoshipOrders) {
                applyPercentageToEligibleCartItems(items, activeSubscriptionPercentage);
                return;
            }

            if (isAutoshipCrawl || isAutoshipPreCrawl) {
                setItemPercentages(prevState => mergeCrawlAutoshipPercentagesState(prevState, items));
            } else {
                applyPercentageToEligibleCartItems(items, initialPercentage);
            }
        } else {
            if (!cart?.hasAutoshipOrders) {
                applyPercentageToEligibleCartItems(items, initialPercentage);
            }
        }
    }, [autoshipDiscountUpdatesEnabled, isAutoshipCrawl, isAutoshipPreCrawl, cart]);

    const hideBanner = useCallback(() => {
        setIsBannerVisible(false);
        wasBannerDismissed.current = true;
    }, []);

    const contextValue = useMemo(
        () => ({
            bannerPercentage,
            itemPercentages,
            isBannerVisible,
            hideBanner,
        }),
        [itemPercentages, isBannerVisible, hideBanner]
    );

    return <AutoshipPercentageContext.Provider value={contextValue}>{children}</AutoshipPercentageContext.Provider>;
}
